import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useState } from 'react';
import { Bell, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, EmptyState, Skeleton } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import { PageHeader } from '@/components/ui/page-header';
import { api, cn } from '@/lib/utils';

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'sent', label: 'Sent' },
  { key: 'failed', label: 'Failed' },
] as const;

export function RemindersPage() {
  const [status, setStatus] = useState<string>('all');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['reminders', status, page],
    queryFn: () =>
      api<{
        logs: Array<{ id: string; templateKey: string; recipientEmail: string; status: string; sentAt: string }>;
        hasMore: boolean;
      }>(`/api/v1/reminders?page=${page}&status=${status}`),
    placeholderData: keepPreviousData,
  });

  const changeStatus = (s: string) => {
    setStatus(s);
    setPage(1);
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Reminder activity" description="Every renewal email sent from your account." />

      <div className="inline-flex gap-0.5 rounded-lg border border-border bg-bg-subtle/70 p-1">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => changeStatus(f.key)}
            className={cn(
              'rounded-md px-3 py-1.5 text-[13px] font-medium transition-all',
              status === f.key ? 'bg-bg-surface text-text-primary shadow-xs' : 'text-text-secondary hover:text-text-primary',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Card noPadding>
        <div className="scroll-slim overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-bg-muted text-left text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
                <th className="px-5 py-3">Reminder</th>
                <th className="px-2 py-3">Recipient</th>
                <th className="px-2 py-3">Status</th>
                <th className="px-5 py-3 text-right">Sent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-5 py-3"><Skeleton className="h-4 w-40" /></td>
                    <td className="px-2 py-3"><Skeleton className="h-4 w-44" /></td>
                    <td className="px-2 py-3"><Skeleton className="h-6 w-20 rounded-full" /></td>
                    <td className="px-5 py-3"><Skeleton className="ml-auto h-4 w-28" /></td>
                  </tr>
                ))
              ) : data?.logs?.length ? (
                data.logs.map((log) => (
                  <tr key={log.id} className="transition-colors hover:bg-bg-subtle/60">
                    <td className="px-5 py-3 font-medium capitalize">{log.templateKey.replace(/_/g, ' ')}</td>
                    <td className="px-2 py-3 text-text-secondary">{log.recipientEmail}</td>
                    <td className="px-2 py-3"><StatusBadge status={log.status} /></td>
                    <td className="px-5 py-3 text-right text-text-secondary">
                      {new Date(log.sentAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>
                    <EmptyState
                      icon={Bell}
                      title="No reminders sent yet"
                      description="Once your agreements approach renewal, reminder emails will appear here."
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {(page > 1 || data?.hasMore) && (
          <div className="flex items-center justify-between border-t border-border px-5 py-3">
            <span className="text-[13px] text-text-muted">Page {page}</span>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <ChevronLeft className="h-3.5 w-3.5" /> Previous
              </Button>
              <Button variant="secondary" size="sm" disabled={!data?.hasMore} onClick={() => setPage((p) => p + 1)}>
                Next <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
